"use client";
import { useEffect, useState } from "react";
import { type Step } from "./surveySteps";
import { Option } from "./surveySteps";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { getSurveySteps, postSurveyFill } from "../services/survey/surveyService";
import { useNavigate } from "react-router-dom";
import { dictionary } from "../utils/dictionary";
import { Language } from "../utils/dictionary";
import { useAuth } from "../context/AuthContext";

export default function InitialSurvey() {
    const [answers, setAnswers] = useState<Record<string, any>>({});
    const [currentStepId, setCurrentStepId] = useState<string | undefined>(undefined);
    const [history, setHistory] = useState<string[]>([]);
    const [surveySteps, setSurveySteps] = useState<Step[]>([]);
    const [currentStep, setCurrentStep] = useState<Step | undefined>(undefined);
    const { language } = useAuth();

    useEffect(() => {
        getSurveySteps("default")
            .then((steps) => {
                setSurveySteps(steps.data as Step[]);
                setCurrentStepId(steps.data[0].id);
            });

    }, []);

    useEffect(() => {
        if (surveySteps.length > 0 && currentStepId) {
            setCurrentStep(surveySteps.find((s: Step) => s.id === currentStepId) as Step | undefined);
        }

    }, [surveySteps, currentStepId]);


    if (!currentStep) return null;

    const handleAnswer = async (value: any) => {
        await new Promise((resolve) => setTimeout(resolve, 500));

        const updated = { ...answers, [currentStep.id]: value };
        setAnswers(updated);

        let nextId: string | undefined;

        if (typeof value === "object" && value?.next) {
            nextId = value.next;
        } else if (typeof currentStep.next_step === "string" && currentStep.next_step) {
            nextId = currentStep.next_step;
        }

        if (currentStep.options) {
            const selectedOpt = currentStep.options.find((opt) => opt.value === value);
            if (selectedOpt?.next) nextId = selectedOpt.next;
        }

        if (nextId === "summary") {
            handleSubmit(updated);
        }

        if (nextId) {
            setHistory((prev) => [...prev, currentStep.id]);
            setCurrentStepId(nextId);
        } else {
            handleSubmit(updated);
        }
    };

    const handleSubmit = async (data: Record<string, any>) => {
        const payload = {
            entity_type:"user",
            entity_id: localStorage.getItem("user_id") || "", 
            survey_id: "initial",
            answers: data
        }
        payload.answers.summary = true;
        await postSurveyFill(payload, 'default')
            .then((res: any) => {
                console.log(res);
            })
            .catch((err: any) => {
                console.error(err);
            });
    };

    const handleBack = () => {
        setHistory((prev) => {
            if (prev.length === 0) return prev;
            const newHistory = [...prev];
            const prevStepId = newHistory.pop();
            if (prevStepId) setCurrentStepId(prevStepId);
            return newHistory;
        });
    };

    return (
        <div className="w-full h-full absolute top-0 left-0 bg-[#F5F6FA] z-[9999] flex flex-col items-center justify-center font-plus-jakarta-sans">
            <div className="p-8 rounded-2xl w-[90%] max-w-[50%] text-center overflow-y-auto">
                {currentStep.type !== "summary" && (
                    <h1 className="font-bold mb-3 text-[58px] leading-[1] mb-8 text-start">
                        {dictionary[currentStep.question_text][language as Language] || currentStep.question_text}
                    </h1>
                )}
                {currentStep.description && (
                    <p className="text-gray-500 mb-6 text-start">
                        {dictionary[currentStep.description][language as Language] || currentStep.description}
                    </p>
                )}

                {currentStep.type === "single" && currentStep.options && (
                    <div className="flex flex-col gap-3">
                        {currentStep.options.map((opt, index) => (
                            <ButtonAnswer
                                key={index.toString()}
                                onClick={() => handleAnswer(opt.value)}
                                option={opt}
                                language={language}
                            />
                        ))}
                    </div>
                )}

                {currentStep.type === "multi" && currentStep.options && (
                    <MultiSelectStep
                        options={currentStep.options}
                        onSubmit={(values) => handleAnswer(values)}
                        language={language}
                    />
                )}

                {currentStep.type === "textarea" && (
                    <TextAreaStep 
                        language={language}
                        key={currentStep.id.toString()}
                        onSubmit={(value) => handleAnswer(value)} 
                    />
                )}

                {currentStep.type === "summary" && <SummaryStep answers={answers} language={language} />}

                {currentStep.type !== "summary" && history.length > 0 && (
                    <div
                        onClick={handleBack}
                        className="mt-4 flex items-center gap-2 cursor-pointer hover:text-black transition-all"
                    >
                        <ChevronLeft className="w-5 h-5 text-gray-500" />
                        <p className="text-1xl font-medium text-gray-500 text-start">
                            {dictionary["back"][language as Language] || "Volver"}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

const ButtonAnswer = ({ key, onClick, option, language }: { key: string, onClick: () => void, option: Option, language: Language }) => {
    const [selected, setSelected] = useState<any>(null);
    const handleClick = () => {
        setSelected({ value: option.value, key: option.next });
        onClick();
    }

    return (
        <button
            key={key}
            onClick={handleClick}
            className={`
                text-2xl 
                py-8 
                px-6 
                font-medium 
                hover:shadow-md 
                text-black 
                py-2 px-4 
                rounded-xl 
                border 
                border-gray-200 
                transition 
                shadow-sm 
                ${(selected?.value === option.value && selected?.key === option.next) ? "bg-[#0E172A] text-white" : "bg-white"}
            `}
        >
            {dictionary[option.label][language as Language] || option.label}
        </button>
    );
}

interface MultiSelectStepProps {
    options: Step["options"];
    onSubmit: (values: string[]) => void;
    language: Language;
}

function MultiSelectStep({ options = [], onSubmit, language }: MultiSelectStepProps) {
    const [selected, setSelected] = useState<string[]>([]);
    const toggleOption = (value: string) => {
        setSelected((prev) =>
            prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
        );
    };

    return (
        <div className="flex flex-col gap-3">
            {options.map((opt) => (
                <button
                    key={opt.value.toString()}
                    onClick={() => toggleOption(opt.value.toString())}
                    className={`border py-8 px-6 rounded-xl transition text-start text-2xl font-bold hover:shadow-md ${selected.includes(opt.value.toString())
                        ? "bg-[#0E172A] text-white"
                        : "bg-white text-gray-700 border-gray-300"
                        }`}
                >
                    {dictionary[opt.label][language as Language] || opt.label}
                </button>
            ))}
            <ButtonContinue onClick={() => onSubmit(selected)} disabled={selected.length === 0} text={dictionary["continue"][language as Language] || "Continuar"} />
        </div>
    );
}

interface TextAreaStepProps {
    onSubmit: (value: string) => void;
    language: Language;
}

function TextAreaStep({ onSubmit, language }: TextAreaStepProps) {
    const [value, setValue] = useState("");
    return (
        <div className="flex flex-col gap-4">
            <textarea
                className="border rounded-lg p-3 w-full h-[200px] resize-none focus:outline-none focus:ring-2 focus:ring-[#0E172A]"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={dictionary["write-your-answer-here"][language as Language] || "Escribe tu respuesta aquí..."}
            />
            <ButtonContinue onClick={() => onSubmit(value)} disabled={!value.trim()} text={dictionary["continue"][language as Language] || "Continuar"} />
        </div>
    );
}

function SummaryStep({ answers, language }: { answers: Record<string, any>, language: Language }) {
    const navigate = useNavigate();
    
    return (
        <div className="flex flex-col items-center justify-center">
            <div className="flex items-center justify-center bg-[#0E172A] rounded-full p-4 mb-6">
                <Check className="w-16 h-16 text-white" />
            </div>
            <h1 className="font-bold mb-3 text-[58px] leading-[1] mb-8 text-[#0E172A]">{dictionary["all-set"][language as Language] || "Todo listo"}</h1>
            <ButtonContinue onClick={() => { navigate("/home") }} disabled={false} text={dictionary["go-to-account"][language as Language] || "Ir a mi cuenta"} />
        </div>
    );
}

const ButtonContinue = ({ onClick, disabled, text = "Continuar" }: { onClick: () => void, disabled: boolean, text?: string }) => {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className="flex items-center gap-2 mt-4 bg-[#0E172A] hover:bg-[#0E172A]/80 text-2xl font-bold text-white py-4 px-6 w-max rounded-xl disabled:opacity-40 transition-all duration-300 hover:scale-105"
        >
            {text}
            <ChevronRight className="w-8 h-8 text-white" />
        </button>
    );
}
